<template>
  <div class="label-tags-input" @click="focusInput">
    <div class="tags-field">
      <span v-for="label in value" :key="label" class="tag">
        {{ label }}
        <button type="button" class="tag-remove" title="Remove label" @click.stop="remove(label)">&times;</button>
      </span>
      <input
        ref="input"
        v-model="draft"
        type="text"
        class="tag-input"
        :placeholder="value.length ? 'Add label...' : placeholder"
        @keydown.enter.prevent="commitDraft"
        @keydown.188.prevent="commitDraft"
        @keydown.delete="maybeRemoveLast"
        @blur="commitDraft"
      />
    </div>
    <div v-if="filteredSuggestions.length" class="tag-suggestions">
      <button
        v-for="suggestion in filteredSuggestions"
        :key="suggestion"
        type="button"
        class="tag-suggestion"
        @mousedown.prevent="add(suggestion)"
      >+ {{ suggestion }}</button>
    </div>
  </div>
</template>

<script>
/**
 * Tag/chip editor for feed labels: labels render as removable chips, typing
 * Enter or a comma adds the draft, backspace on an empty input removes the
 * last chip, and labels already in use elsewhere are offered as one-click
 * suggestions.
 */
export default {
  name: 'RssLabelsInput',

  props: {
    // The current labels (v-model)
    value: {
      type: Array,
      default: () => []
    },
    // All known labels, offered as quick-add suggestions
    suggestions: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Add label...'
    }
  },

  data() {
    return {
      draft: ''
    }
  },

  computed: {
    filteredSuggestions() {
      const current = this.value.map(v => v.toLowerCase())
      const search = this.draft.trim().toLowerCase()

      return this.suggestions
        .filter(s => !current.includes(s.toLowerCase()))
        .filter(s => !search || s.toLowerCase().includes(search))
        .slice(0, 8)
    }
  },

  methods: {
    focusInput() {
      this.$refs.input && this.$refs.input.focus()
    },

    add(label) {
      const clean = (label || '').trim()

      if (!clean) return

      const exists = this.value.some(v => v.toLowerCase() === clean.toLowerCase())

      if (!exists) {
        this.$emit('input', this.value.concat([clean]))
      }

      this.draft = ''
    },

    remove(label) {
      this.$emit('input', this.value.filter(v => v !== label))
    },

    commitDraft() {
      if (this.draft.trim()) {
        this.add(this.draft)
      }
    },

    maybeRemoveLast() {
      if (!this.draft && this.value.length) {
        this.remove(this.value[this.value.length - 1])
      }
    }
  }
}
</script>

<style scoped lang="scss">
.label-tags-input {
  width: 100%;
  margin-bottom: 0.5rem;
}

.tags-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
  min-height: 2.4rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  cursor: text;

  &:focus-within {
    border-color: #20B4FF;
  }
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.3rem 0.15rem 0.6rem;
  border-radius: 999px;
  background: rgba(32, 180, 255, 0.18);
  color: #7fd4ff;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}

.tag-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #7fd4ff;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }
}

.tag-input {
  flex: 1;
  min-width: 7rem;
  padding: 0.2rem 0.25rem;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 0.9rem;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }
}

.tag-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.35rem;
}

.tag-suggestion {
  padding: 0.15rem 0.55rem;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  background: transparent;
  color: #94A3B8;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #20B4FF;
    color: #20B4FF;
  }
}
</style>
